import { useEffect, useMemo, useState } from "react";
import { currentFleet } from "../RtuCluster/fleet";
import { Chart } from "primereact/chart";
import { useTranslation } from "react-i18next";

import { RTU } from "../../types/RTU";
import PopupTotalDevices from "./PopupTotalDevices";
import { getStatusColor } from "../../utils/statusConfig";

// Bug 3154: Rebooting is a transient online sub-state for this aggregate
// widget (matches the precedent set by the Dashboard's other RTU-status
// widgets); Unstable gets its own bucket so it is never silently dropped
// from the total.
const isOnlineBucket = (rtu: RTU) => rtu.status === "online" || rtu.status === "rebooting";
const isUnstableBucket = (rtu: RTU) => rtu.status === "unstable";
const isOfflineBucket = (rtu: RTU) => !isOnlineBucket(rtu) && !isUnstableBucket(rtu);

export const TotalDevices = ({ rtus: allRtus }: { rtus: RTU[] }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [selectedRTUs, setSelectedRTUs] = useState<RTU[]>([]);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  const fleet = useMemo(() => currentFleet(allRtus), [allRtus]);
  const rtus = fleet.rows;

  const onlineDevices = rtus.filter(isOnlineBucket).length;
  const unstableDevices = rtus.filter(isUnstableBucket).length;
  const offlineDevices = rtus.filter(isOfflineBucket).length;

  useEffect(() => {
    const statusKeys = ["online", "unstable", "offline"] as const;
    const bucketFilters = {
      online: isOnlineBucket,
      unstable: isUnstableBucket,
      offline: isOfflineBucket,
    } as const;
    const data = {
      labels: statusKeys.map((status) => t(`status.${status === 'offline' ? 'offlineNotReachable' : status}`, { defaultValue: status })),
      datasets: [
        {
          data: [onlineDevices, unstableDevices, offlineDevices],
          backgroundColor: statusKeys.map((status) => getStatusColor(status)),
          hoverBackgroundColor: statusKeys.map((status) => getStatusColor(status)),
        },
      ],
    };

    const options = {
      cutout: "60%",
      onClick: (_event: any, elements: Array<{ index: number }>) => {
        if (elements.length > 0) {
          const statusKey = statusKeys[elements[0].index];
          setSelectedRTUs(rtus.filter(bucketFilters[statusKey]));
          setVisible(true);
        }
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [offlineDevices, onlineDevices, unstableDevices, rtus, t]);

  return (
    <>
      <Chart
        type="doughnut"
        data={chartData}
        options={{ ...chartOptions, maintainAspectRatio: false }}
        style={{ width: "100%", height: "100%", cursor: "pointer" }}
      />
      {visible && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 999,
          }}
        >
          <PopupTotalDevices rtus={selectedRTUs} onClose={() => setVisible(false)} />
        </div>
      )}
    </>
  );
};
